import React, { useState, useEffect, useMemo } from 'react';
import { Send, Loader2, Users, User, Megaphone, Sparkles, CheckCircle2, Bell } from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Envoyer un message dans la boîte de réception d'un utilisateur (ou de tous).
// Les paiements validés / rejetés / déclarés, la bienvenue, etc. sont envoyés automatiquement ;
// cette page sert aux annonces et messages manuels. Le client ne peut pas répondre.
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

export default function AdminMessaging() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [target, setTarget] = useState('all');          // 'all' | user_id
  const [search, setSearch] = useState('');
  const [type, setType] = useState('info');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/clients-credits`, { headers: authHeaders() });
        const d = await res.json();
        if (d.success) setUsers(d.users);
      } catch { /* silencieux */ }
      setLoadingUsers(false);
    })();
  }, []);

  // Tous les utilisateurs d'emblée ; la saisie ne fait que filtrer la liste
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? users.filter(u => `${u.name} ${u.email} ${u.phone}`.toLowerCase().includes(q)) : users;
  }, [users, search]);

  const chosen = users.find(u => u.user_id === target);
  const canSend = title.trim() && body.trim() && (target === 'all' || chosen) && !sending;

  const send = async () => {
    if (target === 'all' && !confirmAll) { setConfirmAll(true); return; }
    setSending(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/send`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ target, title: title.trim(), body: body.trim(), type }),
      });
      const d = await res.json();
      if (d.success) {
        setResult({ ok: true, text: target === 'all' ? (d.message || 'Envoi en cours.') : `Message envoyé à ${chosen?.name || 'l\'utilisateur'}.` });
        setTitle(''); setBody(''); setConfirmAll(false);
      } else setResult({ ok: false, text: d.message || "Échec de l'envoi." });
    } catch { setResult({ ok: false, text: 'Erreur réseau.' }); }
    setSending(false);
  };

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <Bell className="w-8 h-8 text-fuchsia-400" /> Messagerie
        </h1>
        <p className="text-sm text-blue-200 mt-1">
          Envoyez un message dans la boîte de réception des utilisateurs (web et mobile). Ils le voient mais ne peuvent pas répondre.
          Les messages de paiement (déclaré, validé, rejeté) et de bienvenue partent automatiquement.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        {/* formulaire */}
        <div className="lg:col-span-3 bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">Destinataire</label>
            <div className="flex gap-2 mb-2">
              <button onClick={() => { setTarget('all'); setConfirmAll(false); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm border transition ${target === 'all' ? 'bg-blue-500/30 border-blue-400/60 text-white' : 'bg-white/5 border-white/10 text-gray-300'}`}>
                <Users className="w-4 h-4" /> Tous les utilisateurs
              </button>
              <button onClick={() => { setTarget(chosen ? chosen.user_id : ''); setConfirmAll(false); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm border transition ${target !== 'all' ? 'bg-blue-500/30 border-blue-400/60 text-white' : 'bg-white/5 border-white/10 text-gray-300'}`}>
                <User className="w-4 h-4" /> Un utilisateur
              </button>
            </div>
            {target !== 'all' && (
              <div>
                {chosen ? (
                  <div className="flex items-center justify-between bg-white/10 rounded-xl px-4 py-2.5 text-sm text-white">
                    <span>{chosen.name} <span className="text-gray-400">· {chosen.email}</span></span>
                    <button onClick={() => setTarget('')} className="text-xs text-blue-300 hover:text-blue-200">Changer</button>
                  </div>
                ) : (
                  <>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder={loadingUsers ? 'Chargement…' : 'Filtrer par nom, email ou téléphone…'}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-400" />
                    <div className="mt-2 text-[11px] text-gray-500">
                      {loadingUsers ? '' : `${matches.length} utilisateur${matches.length > 1 ? 's' : ''}${search.trim() ? ' trouvé' + (matches.length > 1 ? 's' : '') : ''} — cliquez pour choisir`}
                    </div>
                    <div className="mt-1 max-h-72 overflow-y-auto rounded-xl border border-white/10 bg-black/20 divide-y divide-white/5">
                      {loadingUsers && <div className="py-6 text-center"><Loader2 className="w-5 h-5 text-blue-400 animate-spin mx-auto" /></div>}
                      {matches.map(u => (
                        <button key={u.user_id} onClick={() => { setTarget(u.user_id); setSearch(''); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition">
                          <div className="text-sm text-white">{u.name || '(sans nom)'}</div>
                          <div className="text-xs text-gray-400">{u.email && !u.email.endsWith('@phone.teckia.local') ? u.email : u.phone}</div>
                        </button>
                      ))}
                      {!loadingUsers && matches.length === 0 && <div className="py-6 text-center text-sm text-gray-500">Aucun utilisateur trouvé.</div>}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">Type</label>
            <div className="flex gap-2">
              {[['info', 'Annonce', Megaphone], ['promo', 'Offre / promotion', Sparkles]].map(([k, label, Icon]) => (
                <button key={k} onClick={() => setType(k)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm border transition ${type === k ? 'bg-fuchsia-500/25 border-fuchsia-400/50 text-white' : 'bg-white/5 border-white/10 text-gray-300'}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">Titre</label>
            <input value={title} onChange={e => setTitle(e.target.value)} maxLength={200} placeholder="Ex : Nouveau — Boîte de réception disponible"
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">Message</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} maxLength={4000} rows={7} placeholder="Votre message…"
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 resize-y" />
            <div className="text-right text-[11px] text-gray-500 mt-1">{body.length} / 4000</div>
          </div>

          {confirmAll && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 text-sm text-amber-200">
              Ce message sera envoyé à <strong>tous</strong> les utilisateurs ({users.length}). Cliquez à nouveau sur « Envoyer » pour confirmer.
            </div>
          )}
          {result && (
            <div className={`rounded-xl px-4 py-3 text-sm flex items-center gap-2 ${result.ok ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200' : 'bg-rose-500/10 border border-rose-500/30 text-rose-200'}`}>
              {result.ok && <CheckCircle2 className="w-4 h-4" />} {result.text}
            </div>
          )}

          <button onClick={send} disabled={!canSend}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-fuchsia-600 hover:opacity-90 text-white font-medium disabled:opacity-40 transition">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {target === 'all' && confirmAll ? 'Confirmer l\'envoi à tous' : 'Envoyer'}
          </button>
        </div>

        {/* aperçu */}
        <div className="lg:col-span-2">
          <div className="text-xs text-gray-400 mb-2">Aperçu côté client</div>
          <div className={`rounded-2xl border p-4 ${type === 'promo' ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-white/[0.07] border-sky-500/30'}`}>
            <div className="flex items-start gap-3">
              <span className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${type === 'promo' ? 'bg-yellow-500/15 text-yellow-300' : 'bg-sky-500/15 text-sky-300'}`}>
                {type === 'promo' ? <Sparkles className="w-5 h-5" /> : <Megaphone className="w-5 h-5" />}
              </span>
              <div className="min-w-0">
                <div className="text-white font-bold text-[15px] break-words">{title || 'Titre du message'}</div>
                <div className="text-gray-300 text-sm mt-1.5 whitespace-pre-line break-words">{body || 'Votre message apparaîtra ici.'}</div>
                <div className="text-[11px] text-gray-500 mt-2">à l'instant</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
